import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Anchor, LogOut, ArrowRight, Calendar, Check } from "lucide-react";
import { MEMBERSHIP_TIERS } from "../lib/captainsClub";
import { SAILING_EVENTS } from "../lib/events";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "am_member_token";

function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function daysUntil(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export default function MembersLounge() {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/members/login", { replace: true });
  }

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      navigate("/members/login", { replace: true });
      return;
    }
    axios
      .get(`${API}/members/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMember(r.data))
      .catch(() => {
        toast.error("Session expired. Please sign in again.");
        logout();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center text-[color:var(--am-muted)]">
        Loading lounge…
      </div>
    );
  }
  if (!member) return null;

  const tier = MEMBERSHIP_TIERS.find((t) => t.id === member.tier) || MEMBERSHIP_TIERS[0];
  const remaining = daysUntil(member.expires_at);
  const upcoming = SAILING_EVENTS.filter((e) => new Date(e.endISO).getTime() >= Date.now()).slice(0, 3);

  return (
    <div className="min-h-screen bg-sand" data-testid="members-lounge">
      {/* HEADER */}
      <header className="nav-glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-navy">
            <Anchor size={22} strokeWidth={1.5} />
            <span className="font-serif-display text-2xl">Anchor Management</span>
          </Link>
          <button
            onClick={logout}
            className="btn-secondary py-2 px-4 text-[0.72rem]"
            data-testid="member-logout-button"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 pb-10">
        <div className="overline mb-4">Members' Lounge</div>
        <h1 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl text-navy leading-[1.02]">
          Welcome back, {member.full_name.split(" ")[0]}.
        </h1>
        <p className="text-[color:var(--am-muted)] mt-4 max-w-xl leading-relaxed">
          Your charter is always within reach. Below you'll find your perks, your renewal,
          and what's worth booking next.
        </p>
      </section>

      {/* MEMBERSHIP CARD */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-12">
        <div className="bg-navy text-white p-10 grid lg:grid-cols-12 gap-8 items-center" data-testid="member-card">
          <div className="lg:col-span-5">
            <div className="overline text-pink-soft mb-3">Your Rank</div>
            <div className="font-serif-display text-6xl leading-none mb-2" data-testid="member-tier">
              {tier.name}
            </div>
            <div className="italic text-white/70 text-sm">{tier.tagline}</div>
            <div className="mt-6 text-xs tracking-[0.22em] uppercase text-pink-soft">
              Member Code · <span className="font-mono text-white" data-testid="member-code">{member.code}</span>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="overline text-pink-soft mb-3">Renewal</div>
            <div className="font-serif-display text-3xl mb-1">{fmtDate(member.expires_at)}</div>
            <div className="text-xs tracking-[0.22em] uppercase text-pink-soft" data-testid="member-days-remaining">
              {remaining} days remaining
            </div>
            <div className="mt-6 overline text-pink-soft mb-2">Joined</div>
            <div className="text-sm text-white/80">{fmtDate(member.joined_at)}</div>
          </div>
          <div className="lg:col-span-3 flex flex-col gap-3">
            <Link
              to="/book"
              className="bg-pink-soft text-navy px-6 py-4 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-white transition-colors text-center inline-flex items-center justify-center gap-2"
              data-testid="member-book-cta"
            >
              Book a Charter <ArrowRight size={14} />
            </Link>
            <a
              href={`mailto:anchormgtbda@gmail.com?subject=Captain's%20Club%20-%20${tier.name}%20-%20${encodeURIComponent(member.full_name)}`}
              className="border border-white/30 text-white px-6 py-4 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-white/10 transition-colors text-center"
              data-testid="member-contact-cta"
            >
              Email the Captain
            </a>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-12">
        <div className="overline mb-5">Your Perks</div>
        <div className="bg-white border border-[color:var(--am-line)] p-10" data-testid="member-perks">
          <ul className="grid md:grid-cols-2 gap-x-10 gap-y-4">
            {tier.perks.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-navy leading-relaxed">
                <Check size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-navy" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20">
        <div className="flex items-end justify-between mb-5">
          <div className="overline">Worth Booking Next</div>
          <Link to="/events" className="text-[0.7rem] tracking-[0.22em] uppercase text-navy hover:opacity-70 inline-flex items-center gap-1.5">
            All events <ArrowRight size={12} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-5" data-testid="member-events">
          {upcoming.length === 0 ? (
            <div className="md:col-span-3 bg-white border border-[color:var(--am-line)] p-8 text-center text-[color:var(--am-muted)]">
              The season's wrapped — see you next year.
            </div>
          ) : (
            upcoming.map((e) => (
              <article
                key={e.id}
                className="bg-pink-soft p-6 hover:opacity-90 transition-opacity"
                data-testid={`member-event-${e.id}`}
              >
                <div className="flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-navy/70 mb-3">
                  <Calendar size={13} />
                  {e.dateLabel}
                </div>
                <h3 className="font-serif-display text-2xl text-navy leading-tight mb-2">{e.name}</h3>
                <p className="italic text-sm text-navy/70 mb-4">{e.tagline}</p>
                <Link
                  to="/book"
                  state={{ experience: e.experience }}
                  className="text-[0.7rem] tracking-[0.22em] uppercase text-navy hover:opacity-60 inline-flex items-center gap-1.5"
                >
                  Book this date <ArrowRight size={13} />
                </Link>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
